import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { toast } from "sonner";
import { Brand } from "@/components/shared/Common";
import { Gift } from "lucide-react";

export default function RegisterPatient() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", mobile: "", dob: "", address: "",
    country: "United Kingdom", preferred_language: "English",
    emergency_contact_name: "", emergency_contact_phone: "",
    password: "", consent: false, referral_code: (sp.get("ref") || "").toUpperCase(),
  });
  useEffect(() => {
    const ref = (sp.get("ref") || "").toUpperCase();
    if (ref) setF(x => ({ ...x, referral_code: ref }));
  }, [sp]);
  const [loading, setLoading] = useState(false);
  const { registerPatient } = useAuth();
  const nav = useNavigate();

  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.consent) return toast.error("Please accept the consent notice.");
    setLoading(true);
    try {
      await registerPatient(f);
      toast.success(f.referral_code ? "Account created — £10 credit unlocks after your first booking" : "Account created");
      nav("/patient");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="cm-container py-14 flex justify-center">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <Link to="/"><Brand /></Link>
          <h1 className="cm-h3 mt-4">Create your patient account</h1>
          <p className="text-sm text-[#475569] mt-2">Are you a doctor? <Link to="/register/doctor" className="text-[#4a90d9] font-semibold">Register as a doctor</Link></p>
        </div>
        {f.referral_code && (
          <div className="cm-card mb-4 border-[#3daa6e]/30 bg-[#eaf7f0] flex items-start gap-3" data-testid="ref-banner">
            <div className="w-10 h-10 rounded-lg bg-[#3daa6e] text-white flex items-center justify-center flex-shrink-0"><Gift size={18}/></div>
            <div className="text-sm text-[#065f46]">
              <div className="font-semibold">You've been invited to CAREMatch</div>
              <div>Referral code <span className="font-mono">{f.referral_code}</span> applied — you'll get <b>£10 credit</b> after your first booking. Your friend gets £10 too.</div>
            </div>
          </div>
        )}
        <form className="cm-card grid sm:grid-cols-2 gap-4" onSubmit={submit} data-testid="register-patient-form">
          <div><label className="cm-label">First name</label><input className="cm-input" required value={f.first_name} onChange={set("first_name")} data-testid="reg-first-name"/></div>
          <div><label className="cm-label">Last name</label><input className="cm-input" required value={f.last_name} onChange={set("last_name")} data-testid="reg-last-name"/></div>
          <div className="sm:col-span-2"><label className="cm-label">Email</label><input className="cm-input" type="email" required value={f.email} onChange={set("email")} data-testid="reg-email"/></div>
          <div><label className="cm-label">Mobile</label><input className="cm-input" value={f.mobile} onChange={set("mobile")} data-testid="reg-mobile"/></div>
          <div><label className="cm-label">Date of birth</label><input className="cm-input" type="date" value={f.dob} onChange={set("dob")} data-testid="reg-dob"/></div>
          <div className="sm:col-span-2"><label className="cm-label">Address</label><input className="cm-input" value={f.address} onChange={set("address")} data-testid="reg-address"/></div>
          <div><label className="cm-label">Country</label><input className="cm-input" value={f.country} onChange={set("country")} data-testid="reg-country"/></div>
          <div><label className="cm-label">Preferred language</label><input className="cm-input" value={f.preferred_language} onChange={set("preferred_language")} data-testid="reg-language"/></div>
          <div><label className="cm-label">Emergency contact name</label><input className="cm-input" value={f.emergency_contact_name} onChange={set("emergency_contact_name")} data-testid="reg-ec-name"/></div>
          <div><label className="cm-label">Emergency contact phone</label><input className="cm-input" value={f.emergency_contact_phone} onChange={set("emergency_contact_phone")} data-testid="reg-ec-phone"/></div>
          <div className="sm:col-span-2"><label className="cm-label">Password</label><input className="cm-input" type="password" required minLength={8} value={f.password} onChange={set("password")} data-testid="reg-password"/></div>
          <div className="sm:col-span-2"><label className="cm-label">Referral code (optional)</label><input className="cm-input font-mono uppercase" value={f.referral_code} onChange={e=>setF({...f, referral_code: e.target.value.toUpperCase()})} data-testid="reg-referral" placeholder="e.g. EMMA10"/></div>
          <label className="sm:col-span-2 flex items-start gap-2 text-sm text-[#475569]">
            <input type="checkbox" checked={f.consent} onChange={set("consent")} data-testid="reg-consent" className="mt-1"/>
            I agree to CAREMatch's <Link to="/legal/patient-terms" className="text-[#4a90d9] font-semibold">Patient Terms</Link> and <Link to="/legal/privacy" className="text-[#4a90d9] font-semibold">Privacy Policy</Link>.
          </label>
          <button className="cm-btn-primary sm:col-span-2 w-full" disabled={loading} data-testid="reg-submit">{loading?"Creating account…":"Create account"}</button>
        </form>
      </div>
    </div>
  );
}
